// Types
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import _ from 'lodash';
// Slices
import { selectFieldsOfType } from 'slices/field.slice';
import { selectObjectTypesList } from 'slices/objectTypes.slice';
// Utils
import { getFieldsForSelectedType } from 'utils/paddingUtils';
import { EFieldType } from '@timeedit/types/lib/enums';
import type { TTravelTimePaddingRule } from '@timeedit/types/lib/types';
// Components
import { Matrix, TEDrawerSection, TEInput } from '@timeedit/ui-components';
import { Typography } from '@timeedit/ui-components/antd';

const { Text } = Typography;

export interface TravelTimePaddingProps {
  rule: TTravelTimePaddingRule;
  onChange: (rule: TTravelTimePaddingRule) => void;
}

const TravelTimePadding = ({ rule, onChange }: TravelTimePaddingProps) => {
  const types = useSelector(selectObjectTypesList);
  const categoryFields = useSelector(selectFieldsOfType(EFieldType.CATEGORY));
  const fields = useMemo(
    () =>
      getFieldsForSelectedType(rule.controllingTypeId, types, categoryFields),
    [rule.controllingTypeId, types, categoryFields]
  );
  const fieldMapping = useMemo(() => _.keyBy(fields, 'id'), [fields]);
  const categories: string[] = useMemo(() => {
    const selectedField = fieldMapping[rule.controllingFieldId];
    return _.get(selectedField, 'categories', []);
  }, [rule, fieldMapping]);
  return (
    <div>
      <TEDrawerSection label="Controlling objects">
        <div className="section-col">
          <TEInput.Select
            label="Object type"
            tooltip="Object type"
            value={
              (rule.controllingTypeId && rule.controllingTypeId.toString()) ||
              undefined
            }
            onChange={(controllingTypeId) => {
              onChange({
                ...rule,
                controllingTypeId:
                  controllingTypeId && Number(controllingTypeId),
                distances: [],
              });
            }}
            options={types.map((type) => ({
              label: type.name,
              value: type.id.toString(),
            }))}
            placeholder="Select"
            data-testid="object-type"
          />
          <TEInput.Select
            label="Field"
            tooltip="Field"
            value={
              (rule.controllingFieldId && rule.controllingFieldId.toString()) ||
              undefined
            }
            onChange={(controllingFieldId) =>
              onChange({
                ...rule,
                controllingFieldId:
                  controllingFieldId && Number(controllingFieldId),
                distances: [],
              })
            }
            options={fields.map((field) => ({
              label: field.name,
              value: field.id.toString(),
            }))}
            placeholder="Select"
            data-testid="field"
          />
        </div>
      </TEDrawerSection>
      <TEDrawerSection label="Padding">
        {categories.length ? (
          <Matrix
            objects={categories}
            paddings={(rule.distances || []).map((dist) => ({
              objects: dist.fieldValues,
              padding: Math.round(dist.padding / 60),
            }))}
            onChange={(paddings) => {
              // Prevent re-rendering when there is no objects
              if (paddings.length === 0) return;
              onChange({
                ...rule,
                distances: paddings.map((pad) => ({
                  fieldValues: pad.objects,
                  padding: pad.padding * 60,
                })),
              });
            }}
          />
        ) : (
          <Text italic>
            Select controlling Object type and Field to enter padding
          </Text>
        )}
      </TEDrawerSection>
    </div>
  );
};

export default TravelTimePadding;
